import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';
import * as legendApi from '../services/legendApi';
import { LegendCard, LegendCardSkeleton } from '../components/legend';
import styles from './ProfilePage.module.css';

export default function ProfilePage() {
    const { t } = useLanguage();
    const { user, isLoggedIn } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState('uploads'); // uploads, legends, favorites
    const [uploads, setUploads] = useState([]);
    const [legends, setLegends] = useState([]);
    const [favorites, setFavorites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [legendsLoading, setLegendsLoading] = useState(false);
    const [message, setMessage] = useState(location.state?.message || '');
    const [riderGlory, setRiderGlory] = useState(null);

    useEffect(() => {
        if (isLoggedIn) {
            loadData();
        }
    }, [isLoggedIn]);

    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => setMessage(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [uploadsRes, favoritesRes, legendsRes, gloryRes] = await Promise.all([
                api.getMyConfigurations(),
                api.getMyFavorites(),
                legendApi.getMyLegends(),
                legendApi.getMyGlory(),
            ]);

            if (uploadsRes.success) {
                setUploads(uploadsRes.data || []);
            }
            if (favoritesRes.success) {
                setFavorites(favoritesRes.data || []);
            }
            if (legendsRes.success) {
                setLegends(legendsRes.data || []);
            }
            if (gloryRes.success) {
                setRiderGlory(gloryRes.data);
            }
        } catch (err) {
            console.error('Failed to load profile data:', err);
        } finally {
            setLoading(false);
        }
    };

    // 删除传奇
    const handleDeleteLegend = async (legendId) => {
        if (!window.confirm(t('profile.confirmDeleteLegend') || 'Are you sure you want to delete this legend?')) {
            return;
        }

        try {
            const result = await legendApi.deleteLegend(legendId);
            if (result.success) {
                setLegends(prev => prev.filter(l => l.id !== legendId));
                setMessage(t('profile.legendDeleteSuccess') || 'Legend deleted successfully');
            }
        } catch (err) {
            console.error('Delete legend failed:', err);
        }
    };

    const handleDelete = async (configId) => {
        if (!window.confirm(t('profile.confirmDelete') || 'Are you sure you want to delete this configuration?')) {
            return;
        }

        try {
            const result = await api.deleteConfiguration(configId);
            if (result.success) {
                setUploads(prev => prev.filter(c => c.id !== configId));
                setMessage(t('profile.deleteSuccess') || 'Configuration deleted successfully');
            }
        } catch (err) {
            console.error('Delete failed:', err);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            draft: { label: t('profile.statusDraft') || 'Draft', class: styles.draft },
            pending: { label: t('profile.statusPending') || 'Pending Review', class: styles.pending },
            approved: { label: t('profile.statusApproved') || 'Approved', class: styles.approved },
            rejected: { label: t('profile.statusRejected') || 'Rejected', class: styles.rejected },
        };
        const config = statusConfig[status] || statusConfig.draft;
        return <span className={`${styles.statusBadge} ${config.class}`}>{config.label}</span>;
    };

    if (!isLoggedIn) {
        return (
            <div className={styles.notLoggedIn}>
                <h2>{t('profile.loginRequired') || 'Login Required'}</h2>
                <p>{t('profile.loginMessage') || 'Please login to view your profile.'}</p>
            </div>
        );
    }

    return (
        <div className={styles.profile}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.userInfo}>
                    <div className={styles.avatar}>
                        {user?.nickname?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className={styles.userDetails}>
                        <h1>{user?.nickname}</h1>
                        <p>{user?.email}</p>
                    </div>
                </div>
                <Link to="/gallery/upload" className={styles.uploadBtn}>
                    + {t('profile.newUpload') || 'New Upload'}
                </Link>
            </div>

            {/* Message */}
            {message && (
                <div className={styles.message}>
                    {message}
                </div>
            )}

            {/* 骑士荣耀摘要 */}
            {riderGlory && (
                <div className={styles.gloryBanner}>
                    <div className={styles.gloryItem}>
                        <span className={styles.gloryIcon}>{legendApi.getRiderLevelInfo(riderGlory.level)?.icon || '🔰'}</span>
                        <span className={styles.gloryLabel}>{legendApi.getRiderLevelInfo(riderGlory.level, t('lang') || 'zh')?.title || '新手骑士'}</span>
                    </div>
                    <div className={styles.gloryItem}>
                        <span className={styles.gloryValue}>{legendApi.formatNumber(riderGlory.total_salutes_received || 0)}</span>
                        <span className={styles.gloryLabel}>{t('profile.salutesReceived') || '获得致敬'}</span>
                    </div>
                    <div className={styles.gloryItem}>
                        <span className={styles.gloryValue}>{riderGlory.legends_count || 0}</span>
                        <span className={styles.gloryLabel}>{t('profile.legendsForged') || '铸造传奇'}</span>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className={styles.tabs}>
                <button
                    className={`${styles.tab} ${activeTab === 'uploads' ? styles.active : ''}`}
                    onClick={() => setActiveTab('uploads')}
                >
                    {t('profile.myUploads') || 'My Uploads'} ({uploads.length})
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'legends' ? styles.active : ''}`}
                    onClick={() => setActiveTab('legends')}
                >
                    🔥 {t('profile.myLegends') || 'My Legends'} ({legends.length})
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'favorites' ? styles.active : ''}`}
                    onClick={() => setActiveTab('favorites')}
                >
                    {t('profile.myFavorites') || 'My Favorites'} ({favorites.length})
                </button>
            </div>

            {/* Content */}
            <div className={styles.content}>
                {loading ? (
                    <div className={styles.loading}>
                        {t('common.loading') || 'Loading...'}
                    </div>
                ) : activeTab === 'uploads' ? (
                    uploads.length === 0 ? (
                        <div className={styles.empty}>
                            <p>{t('profile.noUploads') || "You haven't uploaded any configurations yet."}</p>
                            <Link to="/gallery/upload" className={styles.emptyBtn}>
                                {t('profile.uploadFirst') || 'Upload Your First Configuration'}
                            </Link>
                        </div>
                    ) : (
                        <div className={styles.grid}>
                            {uploads.map(config => (
                                <div key={config.id} className={styles.card}>
                                    <div className={styles.cardImage}>
                                        {config.cover_image_url ? (
                                            <img src={config.cover_image_url} alt={config.title} />
                                        ) : (
                                            <div className={styles.placeholder}>{t('common.noImage')}</div>
                                        )}
                                        {getStatusBadge(config.status)}
                                    </div>
                                    <div className={styles.cardContent}>
                                        <h3>{config.title}</h3>
                                        <p className={styles.price}>¥{config.total_price?.toLocaleString()}</p>
                                        <div className={styles.stats}>
                                            <span>{config.likes_count || 0} {t('detail.likes')}</span>
                                            <span>{config.favorites_count || 0} {t('detail.favorites')}</span>
                                        </div>
                                        <div className={styles.cardActions}>
                                            <Link to={`/gallery/${config.id}`} className={styles.viewBtn}>
                                                {t('common.view') || 'View'}
                                            </Link>
                                            {config.status !== 'approved' && (
                                                <button
                                                    className={styles.deleteBtn}
                                                    onClick={() => handleDelete(config.id)}
                                                >
                                                    {t('common.delete') || 'Delete'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                ) : activeTab === 'legends' ? (
                    /* 我的传奇 */
                    legends.length === 0 ? (
                        <div className={styles.empty}>
                            <div className={styles.emptyIcon}>🔥</div>
                            <p>{t('profile.noLegends') || "You haven't forged any legends yet."}</p>
                            <p className={styles.emptyHint}>
                                {t('profile.legendsHint') || "Upload a configuration and forge it into a legend to share with the community!"}
                            </p>
                            <Link to="/gallery/upload" className={styles.emptyBtn}>
                                {t('profile.forgeLegend') || 'Forge Your First Legend'}
                            </Link>
                        </div>
                    ) : (
                        <div className={styles.legendsGrid}>
                            {legends.map(legend => (
                                <div key={legend.id} className={styles.legendCardWrapper}>
                                    <LegendCard
                                        legend={legend}
                                        onClick={() => navigate(`/legend/${legend.id}`)}
                                    />
                                    <div className={styles.legendActions}>
                                        <Link to={`/legend/${legend.id}`} className={styles.viewBtn}>
                                            {t('common.view') || 'View'}
                                        </Link>
                                        <button
                                            className={styles.deleteBtn}
                                            onClick={() => handleDeleteLegend(legend.id)}
                                        >
                                            {t('common.delete') || 'Delete'}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                ) : (
                    favorites.length === 0 ? (
                        <div className={styles.empty}>
                            <p>{t('profile.noFavorites') || "You haven't saved any favorites yet."}</p>
                            <Link to="/gallery" className={styles.emptyBtn}>
                                {t('profile.browseGallery') || 'Browse Gallery'}
                            </Link>
                        </div>
                    ) : (
                        <div className={styles.grid}>
                            {favorites.map(config => (
                                <Link
                                    to={`/gallery/${config.id}`}
                                    key={config.id}
                                    className={styles.card}
                                >
                                    <div className={styles.cardImage}>
                                        {config.cover_image_url ? (
                                            <img src={config.cover_image_url} alt={config.title} />
                                        ) : (
                                            <div className={styles.placeholder}>{t('common.noImage')}</div>
                                        )}
                                        {config.is_official && (
                                            <span className={styles.officialBadge}>{t('common.official')}</span>
                                        )}
                                    </div>
                                    <div className={styles.cardContent}>
                                        <h3>{config.title}</h3>
                                        <p className={styles.author}>
                                            {t('gallery.byAuthor')} {config.user?.nickname || t('common.anonymous')}
                                        </p>
                                        <p className={styles.price}>¥{config.total_price?.toLocaleString()}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )
                )}
            </div>
        </div>
    );
}
